import React, { Fragment, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useEditorContext } from '../SMLEditorContext';
import { SMLBlockForm } from '../forms/SMLBlockForm';
import { SMLBlockSheet } from './SMLBlockSheet';
import { blockMessages } from './blockMessages';
import { describeBlock } from './describeBlock';
import { isNewAutoAdded } from './autoAdded';
import { isOnceOnlyBlock, rawDataOfWidget } from './rawDataOfWidget';
import { BlockMenuAction } from './SMLBlockMenu';
import { SMLBlockRow } from './SMLBlockRow';
import { SMLBlockInsertGap } from './SMLBlockInsertGap';
import { SMLEmptyStarters } from './SMLEmptyStarters';
import { SMLAutoAddNote } from './SMLAutoAddNote';
import { useBlockOrder } from './useBlockOrder';
import { useBlockDrag } from './useBlockDrag';
import { useBlockActions } from './useBlockActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLBlocksTabProps {
  /** The rows in page order, without a row whose delete waits for the toast. */
  widgets: readonly SerializedWidget[];
  /** Every block of the page. */
  pageWidgets: readonly SerializedWidget[];
}

/** The Blocks tab (design a1, a2, a3, a7, e3): the rows, "+ Add here" and the forms in place. */
const SMLBlocksTabComponent: React.FC<SMLBlocksTabProps> = ({ widgets, pageWidgets }) => {
  const intl = useIntl();
  const context = useEditorContext();
  const { accountId, smartLinkId, url, hoveredUUID, setHovered, requestDelete, showNotice, isCompact, artistName } =
    context;
  const { ordered, moveTo, moveBy } = useBlockOrder({ accountId, smartLinkId, widgets });
  const drag = useBlockDrag({ ordered, moveTo });
  const onError = useCallback(() => showNotice(intl.formatMessage(blockMessages.saveFailed)), [intl, showNotice]);
  const { toggleHidden, duplicate } = useBlockActions({ accountId, smartLinkId, onError });
  const descriptions = useMemo(
    () => new Map(ordered.map((widget) => [widget.uuid, describeBlock(intl, widget, { artistName })])),
    [artistName, intl, ordered]
  );
  const openWidget = useMemo(
    () => ordered.find((widget) => widget.uuid === url.openBlockUUID),
    [ordered, url.openBlockUUID]
  );
  const dragFrom = drag.drag === null ? -1 : ordered.findIndex((widget) => widget.uuid === drag.drag?.widgetUUID);

  const onToggle = useCallback(
    (widgetUUID: string) => (url.openBlockUUID === widgetUUID ? url.closeLayer() : url.openBlock(widgetUUID)),
    [url]
  );
  const onAction = useCallback(
    (widget: SerializedWidget, action: BlockMenuAction) => {
      if (action === 'edit') {
        url.openBlock(widget.uuid);
      } else if (action === 'up' || action === 'down') {
        moveBy(widget.uuid, action === 'up' ? -1 : 1);
      } else if (action === 'duplicate') {
        duplicate(widget);
      } else if (action === 'toggleHidden') {
        toggleHidden(widget);
      } else {
        requestDelete(widget);
      }
    },
    [duplicate, moveBy, requestDelete, toggleHidden, url]
  );
  const openPickerAfter = useCallback((afterUUID: string) => url.openPicker(afterUUID), [url]);
  const openPickerAtEnd = useCallback(() => url.openPicker(null), [url]);

  if (ordered.length === 0) {
    return <SMLEmptyStarters />;
  }

  const addButton = (
    <button type="button" className={styles.addBlock} onClick={openPickerAtEnd} data-testid="AddBlock">
      <FontAwesomeIcon icon={faPlus} />
      {intl.formatMessage(blockMessages.addBlock)}
    </button>
  );

  return (
    <div className={styles.tab}>
      {context.isBioLink && <SMLAutoAddNote widgets={ordered} />}
      <div className={styles.head}>
        <h3 className={styles.title}>{intl.formatMessage(blockMessages.listTitle)}</h3>
        <button type="button" className={styles.headAdd} onClick={openPickerAtEnd}>
          <FontAwesomeIcon icon={faPlus} />
          {intl.formatMessage(blockMessages.addBlock)}
        </button>
      </div>
      <div className={styles.list}>
        {ordered.map((widget, index) => {
          const description = descriptions.get(widget.uuid);
          if (description === undefined) {
            return null;
          }
          const isOver = drag.drag !== null && drag.drag.overIndex === index && dragFrom !== index;
          let dropSide: 'above' | 'below' | null = null;
          if (isOver) {
            dropSide = dragFrom < index ? 'below' : 'above';
          }
          const isOpen = widget.uuid === url.openBlockUUID;
          return (
            <Fragment key={widget.uuid}>
              <SMLBlockRow
                widget={widget}
                description={description}
                index={index}
                count={ordered.length}
                isOpen={isOpen}
                isHovered={hoveredUUID === widget.uuid}
                isNew={isNewAutoAdded(widget)}
                canDuplicate={!isOnceOnlyBlock(widget) && rawDataOfWidget(widget) !== null}
                dropSide={dropSide}
                drag={drag}
                onToggle={onToggle}
                onAction={onAction}
                onHover={setHovered}
              >
                {isOpen && !isCompact ? (
                  <SMLBlockForm key={widget.uuid} widget={widget} pageWidgets={pageWidgets} />
                ) : undefined}
              </SMLBlockRow>
              {index < ordered.length - 1 && <SMLBlockInsertGap afterUUID={widget.uuid} onAdd={openPickerAfter} />}
            </Fragment>
          );
        })}
      </div>
      {addButton}
      {isCompact && openWidget !== undefined && (
        <SMLBlockSheet
          widget={openWidget}
          description={descriptions.get(openWidget.uuid) ?? null}
          onClose={url.closeLayer}
        >
          <SMLBlockForm key={openWidget.uuid} widget={openWidget} pageWidgets={pageWidgets} />
        </SMLBlockSheet>
      )}
    </div>
  );
};

export const SMLBlocksTab = TypedMemo(SMLBlocksTabComponent);
